import {
  Button,
  FlexBox,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useArticleTranslationUnpublish } from 'articleEditor/hooks/useArticleTranslationUnpublish';
import { LanguageId } from 'lib-knowledge-management-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

type ArticleTranslationUnpublishModalProps = {
  articleId: string;
  articleTitle: string;
  languageId: LanguageId;
  languageName: string;
};

export function ArticleTranslationUnpublishModal({
  articleId,
  articleTitle,
  languageId,
  languageName,
}: ArticleTranslationUnpublishModalProps): JSX.Element {
  const [unpublishFn, { loading }] = useArticleTranslationUnpublish({
    articleId,
    languageId,
  });
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();

  const unpublish = useCallback(() => {
    unpublishFn()
      .then((result) => {
        if (result.errors?.length) {
          throw new Error(result.errors[0].message);
        }
        successNotify({
          title: 'Success',
          text: `"${articleTitle}" (${languageName}) has been unpublished`,
        });
        hideModal();
      })
      .catch(() => {
        errorNotify({
          title: 'Error',
          text: `"${articleTitle}" (${languageName}) could not be unpublished. Please try again or contact support.`,
        });
      });
  }, [
    unpublishFn,
    articleTitle,
    languageName,
    successNotify,
    hideModal,
    errorNotify,
  ]);

  return (
    <ModalWrapper>
      <ModalHead title="Unpublish" onClose={hideModal}></ModalHead>
      <ModalBody>
        <Text.Paragraph>
          Are you sure you want to unpublish{' '}
          <strong>&#34;{articleTitle}&#34;</strong> in {languageName}?
        </Text.Paragraph>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button isLoading={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button isLoading={loading} onClick={unpublish}>
            {loading ? 'Unpublishing...' : 'Unpublish'}
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
